import React from "react";
import HBadge1 from "../../../../hcomponents/HBadge1";

// Employee Management columns configuration
export const employeeColumns = [
  {
    key: "name",
    header: "Name",
    render: user => (
      <div className="flex items-center min-w-0">
        {user.imageUrl ? (
          <img
            src={user.imageUrl}
            alt={`${user.firstName} ${user.lastName}`}
            className="object-cover mr-2 w-8 h-8 rounded-full border"
          />
        ) : (
          <div className="mr-2 w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-medium">
            {(user.firstName?.[0] || "").toUpperCase()}
            {(user.lastName?.[0] || "").toUpperCase()}
          </div>
        )}
        <span className="truncate text-gray-900">
          {user.firstName} {user.lastName}
        </span>
      </div>
    ),
    mobileLabel: "Name",
  },
  {
    key: "email",
    header: "Email",
    render: user => <span className="text-gray-600 block truncate">{user.emailAddress}</span>,
    mobileLabel: "Email",
  },
  {
    key: "role",
    header: "Role",
    render: user => (
      <HBadge1 tone="indigo" size="sm">
        {user.organizationRole?.name || "N/A"}
      </HBadge1>
    ),
    mobileLabel: "Role",
  },
  {
    key: "organization",
    header: "Organization",
    render: user => user.organization?.name || "N/A",
    mobileLabel: "Organization",
  },
  {
    key: "status",
    header: "Status",
    render: user => {
      const mapTone =
        user.status === "ACTIVE"
          ? "green"
          : user.status === "DISABLED"
            ? "red"
            : user.status === "INACTIVE"
              ? "yellow"
              : "gray";
      return (
        <HBadge1 tone={mapTone} size="sm">
          {user.status}
        </HBadge1>
      );
    },
    mobileLabel: "Status",
  },
];

// Function to create actions column (requires handlers passed from parent)
export const createActionsColumn = ({ handleEditUserRole, handleEditUserDetails, handleDeleteUser }) => ({
  key: "actions",
  header: "Actions",
  render: user => (
    <div className="flex items-center gap-2">
      <button
        onClick={() => handleEditUserDetails(user)}
        className="h-8 w-8 p-0 inline-flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
        title="Edit user"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      </button>
      {user.organizationRole?.name !== "Admin" && (
        <button
          onClick={() => handleDeleteUser(user)}
          className={`h-8 w-8 p-0 inline-flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded ${
            user.status === "ACTIVE" ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={user.status === "ACTIVE"}
          title={user.status === "ACTIVE" ? "Can only delete inactive users" : "Delete user"}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      )}
    </div>
  ),
  mobileRender: user => (
    <div className="flex flex-wrap gap-2 mt-2">
      <button
        onClick={() => handleEditUserRole(user)}
        className="px-3 py-1 text-sm text-purple-600 bg-purple-50 rounded"
      >
        Edit Role
      </button>
      <button
        onClick={() => handleEditUserDetails(user)}
        className="px-3 py-1 text-sm text-orange-600 bg-orange-50 rounded"
      >
        Edit Details
      </button>
      <button
        onClick={() => handleDeleteUser(user)}
        className={`px-3 py-1 text-sm text-red-600 bg-red-50 rounded ${
          user.status === "ACTIVE" ? "opacity-50 cursor-not-allowed" : "hover:bg-red-100"
        }`}
        disabled={user.status === "ACTIVE"}
        title={user.status === "ACTIVE" ? "Can only delete inactive users" : "Delete user"}
      >
        Delete
      </button>
    </div>
  ),
});

// Individual Customers columns configuration
export const individualCustomerColumns = [
  {
    key: "name",
    header: "Name",
    render: user => (
      <div className="flex items-center min-w-0">
        {user.imageUrl ? (
          <img
            src={user.imageUrl}
            alt={`${user.firstName} ${user.lastName}`}
            className="object-cover mr-2 w-8 h-8 rounded-full border"
          />
        ) : (
          <div className="mr-2 w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-medium">
            {(user.firstName?.[0] || "").toUpperCase()}
            {(user.lastName?.[0] || "").toUpperCase()}
          </div>
        )}
        <span className="truncate text-gray-900">
          {user.firstName} {user.lastName}
        </span>
      </div>
    ),
    mobileLabel: "Name",
  },
  {
    key: "email",
    header: "Email",
    render: user => <span className="text-gray-600 block truncate">{user.emailAddress}</span>,
    mobileLabel: "Email",
  },
  {
    key: "phone",
    header: "Phone",
    render: user => user.msisdn || "N/A",
    mobileLabel: "Phone",
  },
  {
    key: "status",
    header: "Status",
    render: user => {
      const mapTone =
        user.status === "ACTIVE"
          ? "green"
          : user.status === "DISABLED"
            ? "red"
            : user.status === "INACTIVE"
              ? "yellow"
              : "gray";
      return (
        <HBadge1 tone={mapTone} size="sm">
          {user.status}
        </HBadge1>
      );
    },
    mobileLabel: "Status",
  },
  {
    key: "joined",
    header: "Joined",
    render: user => new Date(user.createdAt).toLocaleDateString(),
    mobileLabel: "Joined",
  },
];
